/* Provides a generic key-value store context and provider component. Useful for many complex "form"
 * patterns.
 */

import React, {createContext, useMemo, useCallback} from 'react'
import {default as objIsEq} from "underscore/modules/isEqual.js"
import {usePState} from 'js/studios/shared/unredux/hooks'

export const KeyValueCtx = createContext()
KeyValueCtx.displayName = "KeyValueCtx "

// addToKey/rmFromKey should work for both primitives and objects. underscore isEqual is really only
// intended for objects, so we wrap it and either use it or === depending on arg types
function isEq(a, b) {
  if (typeof a === "object" || typeof b === "object") {
    return objIsEq(a, b)
  } else {
    return a === b
  }
}

export function KeyValueProvider({
  defaults = {}, // the default values for keys in `data`
  data: propData, // current/initial values if different from defaults. onChange is not called if this changes.
  onChange, // optional handler function for doing something in response to changes. The handler is passed an *object* of changes, as well as the full data
  children,
}) {
  const
    [data, setData] = usePState(propData ?? defaults),

    // update can be a value or a function that will receive the current value, similar to setState
    setKey = useCallback((key, update) => {
      setData(curData => {
        const newVal = typeof(update) === "function" ? update(curData[key]) : update
        return {...curData, [key]: newVal}
      }).then(curData => onChange?.({[key]: curData[key]}, curData))
    }, [onChange, setData]),

    // change multiple keys simulataneously. dataDelta must be an object.
    setMany = useCallback(dataDelta => {
      setData(curData => ({...curData, ...dataDelta})).then(curData => onChange?.(dataDelta, curData))
    }, [onChange, setData]),

    // assumes key is an array, adds item if it is not already in the list
    addToKey = useCallback((key, newItem) => {
      setKey(key, curVal => {
        if (!curVal.some(x => isEq(x, newItem))) {
          return curVal.concat([newItem])
        }
        return curVal
      })
    }, [setKey]),

    // assumes key is an array, removes the item
    rmFromKey = useCallback((key, rmItem) => {
      setKey(key, curVal => curVal.filter(x => !isEq(x, rmItem)))
    }, [setKey]),

    // boundSetterCache does not depend on setKey, but boundSetter does, so transitively this should
    // too even though setKey itself is very unlikely to change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    boundSetterCache = useMemo(() => ({}), [setKey]), // cache of name => fn. This object gets mutated directly.

    /* boundSetter can be used to construct a cached setter function for a given key. E.g. in a form
    * inside this context, you could use `<input onChange={boundSetter('foo')} />` and any changes
    * to the input will effectively call `setKey('foo', input.value)` */
    boundSetter = useCallback(k => {
      if (!boundSetterCache[k]) {
        boundSetterCache[k] = evOrVal => {
          // looks like an event (we use duck typing since we fake events in some wrapper
          // components)
          if (evOrVal?.preventDefault) {
            evOrVal.preventDefault()
            evOrVal = evOrVal.target.value
          }
          setKey(k, evOrVal)
        }
      }
      return boundSetterCache[k]
    }, [setKey, boundSetterCache]),

    ctxVal = useMemo(() => ({
      data, setData, setKey, addToKey, rmFromKey, setMany, boundSetter,
      getKey: k => data[k],
      resetKey: k => setKey(k, defaults[k]),
    }), [defaults, data, setData, setKey, addToKey, rmFromKey, setMany, boundSetter])

  return <KeyValueCtx.Provider value={ctxVal}>{children}</KeyValueCtx.Provider>
}
